import { tw } from "@careinfo/tailwind";
import { Transition } from "@headlessui/react";
import { PlusIcon } from "@heroicons/react/outline";
import {
  Fragment,
  useCallback,
  useContext,
  type Dispatch,
  type SetStateAction,
} from "react";
import { useSearchParams } from "react-router-dom";
import PushNofication from "../PushNotification";
import Chat from "./Chat";
import { NOTIFICATION_OFFSET, NOTIFICATION_TYPE, UNREAD } from "./constants";
import { NotificationContext } from "./context";
import { NotificationHeader } from "./HeaderWithSearch";
import NoticeArea from "./NoticeArea";
import NotificationArea from "./NotificationArea";
import { NotificationTab } from "./Tab";

export function NotificationPanel({
  setShowPannel,
  showPannel,
  panelRef,
}: {
  showPannel: boolean;
  setShowPannel: Dispatch<SetStateAction<boolean>>;
  panelRef: React.RefObject<HTMLDivElement>;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { resetNotifications, active, handleTabChange } =
    useContext(NotificationContext);
  /**
   * @name RefCallback
   *
   * @description
   * - adds searchParams when transition is in view
   * - Removes search params when transition is out of view
   *
   */
  const setRef = useCallback(
    (ref) => {
      const clonedParams = new URLSearchParams(searchParams);
      if (ref) {
        const notificationType = clonedParams.get(NOTIFICATION_TYPE);
        const notificationOffset = clonedParams.get(NOTIFICATION_OFFSET);
        if (!notificationType) {
          clonedParams.set(NOTIFICATION_TYPE, UNREAD);
        }
        if (!notificationOffset) clonedParams.set(NOTIFICATION_OFFSET, "0");
        setSearchParams(clonedParams);
      }
      if (!ref) {
        clonedParams.delete(NOTIFICATION_TYPE);
        clonedParams.delete(NOTIFICATION_OFFSET);
        setSearchParams(clonedParams);
      }
    },
    [searchParams, setSearchParams]
  );

  return (
    <Transition as={Fragment} ref={setRef} unmount appear show={showPannel}>
      <Transition.Child
        as={"div"}
        enter={tw`transition-all duration-300 ease-out`}
        enterFrom={tw`translate-x-full`}
        enterTo={tw`translate-x-0`}
        leave={tw`transition-all duration-200 ease-in`}
        leaveFrom={tw`translate-x-0`}
        leaveTo={tw`translate-x-full`}
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
        className={tw`absolute z-10 right-0 rounded-lg w-[min(100%,400px)] min-h-[calc(100vh-100px)] h-[calc(100vh-100px)] shadow-lg  bg-[#F5F5F5] mt-3`}
      >
        <div className={tw`h-full flex flex-col justify-between w-full`}>
          <div className={tw`flex gap-1 flex-col`}>
            <div
              className={tw`font-bold text-lg w-full py-2 flex items-center justify-between gap-2 px-4`}
              style={{
                boxShadow: "0px 1px 6px 0px #00000026",
              }}
            >
              <div className={tw`font-bold text-lg flex gap-2 pt-2`}>
                <svg
                  width="20"
                  height="22"
                  viewBox="0 0 20 22"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11.73 20C11.5542 20.3031 11.3018 20.5547 10.9982 20.7295C10.6946 20.9044 10.3504 20.9965 10 20.9965C9.64962 20.9965 9.30539 20.9044 9.00177 20.7295C8.69816 20.5547 8.44581 20.3031 8.27 20M16 7C16 5.4087 15.3679 3.88258 14.2426 2.75736C13.1174 1.63214 11.5913 1 10 1C8.4087 1 6.88258 1.63214 5.75736 2.75736C4.63214 3.88258 4 5.4087 4 7C4 14 1 16 1 16H19C19 16 16 14 16 7Z"
                    stroke="#0D0D0D"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Notifications</span>
                <PushNofication />
              </div>
              <button
                onClick={() => setShowPannel(!showPannel)}
                className={tw`focus:outline-none h-5 w-5 rotate-[45deg] inline-block`}
              >
                <PlusIcon />
              </button>
            </div>
            <NotificationHeader active={active} />
            <NotificationTab
              active={active}
              handleTabChange={handleTabChange}
            />
          </div>
          {active === "notification" ? (
            <NotificationArea />
          ) : active === "notice" ? (
            <NoticeArea />
          ) : active === "chat" ? (
            <Chat />
          ) : null}
        </div>
      </Transition.Child>
    </Transition>
  );
}
